import { useRef } from 'react';

const MIN_SWIPE_DISTANCE = 50;

/**
 * useSwipe hook
 *
 * Provides vertical swipe-down gesture detection for closing UI elements on touch devices.
 * Use this hook to add "swipe down to close" functionality, for example, in modals or panels.
 *
 * @param onCloseRequest - Callback invoked when a downward swipe exceeds the minimum distance.
 * @returns Object with onTouchStart, onTouchMove, and onTouchEnd handlers to spread onto a root element.
 *
 * Example usage:
 *   const touchEvents = useSwipe(handleClose);
 *   <div {...touchEvents}>...</div>
 */

export function useSwipe(onCloseRequest: () => void) {
  const touchStartY = useRef<number | null>(null);
  const touchEndY = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartY.current = e.touches[0].clientY;
    touchEndY.current = null;
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    touchEndY.current = e.touches[0].clientY;
  };

  const onTouchEnd = () => {
    if (
      typeof touchStartY.current !== 'number' ||
      typeof touchEndY.current !== 'number'
    ) {
      touchStartY.current = null;
      touchEndY.current = null;
      return;
    }

    const distance = touchEndY.current - touchStartY.current;

    if (distance > MIN_SWIPE_DISTANCE) {
      onCloseRequest();
    }

    touchStartY.current = null;
    touchEndY.current = null;
  };

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd
  };
}
